import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import api from "../lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Plus, Trash2, Save, ArrowRight } from "lucide-react";

const CASE_TYPES = ["Appeal", "Revision", "Review", "Miscellaneous", "Other"];
const PARTY_ROLES = ["Applicant", "Appellant", "Respondent", "Opponent", "Authority", "Other"];
const AUTHORITY_LEVELS = ["Mamlatdar", "Prant", "SDM", "Collector", "District Magistrate", "Other"];
const OUTCOMES = ["Allowed", "Rejected", "Partly Allowed", "Withdrawn", "Other"];
const LAND_UNITS = ["Acres", "Hectares", "Sq. Mtrs."];

const emptyParty = () => ({
  party_role: "Applicant", party_sequence: 1, party_name: "", father_or_spouse_name: "",
  address_line: "", village: "", taluka: "", district: "", state: "Gujarat", pin_code: "",
  advocate_name: "", representative_details: "", is_government_authority: false,
});
const emptyProperty = () => ({
  district: "", taluka: "", village: "", survey_number: "", block_number: "",
  old_survey_number: "", new_survey_number: "", city_survey_number: "",
  revenue_record_ref: "", land_area: "", land_area_unit: "Acres",
  land_use_type: "", disputed_property_description: "",
});
const emptyLowerOrder = () => ({
  authority_level: "Collector", authority_name: "", office_location: "",
  order_number: "", order_date: "", challenged_order_summary: "",
  outcome_of_lower_order: "Rejected",
});

export default function CreateCase() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("master");
  const [saving, setSaving] = useState(false);
  const [caseDoc, setCaseDoc] = useState({
    case_number: "", case_year: String(new Date().getFullYear()), case_type: "Appeal",
    department_name: "Revenue Department, Government of Gujarat",
    office_name: "Special Secretary (Revenue), SSRD",
    district: "", taluka: "", village: "",
    subject_title: "", legal_act: "Gujarat Land Revenue Code, 1879",
    legal_section: "Section 211", filing_date: "", hearing_date: "", order_date: "",
    language: "Gujarati", case_status: "Drafting Pending",
  });
  const [parties, setParties] = useState([emptyParty()]);
  const [properties, setProperties] = useState([emptyProperty()]);
  const [lowerOrders, setLowerOrders] = useState([emptyLowerOrder()]);

  const save = async (continueTo) => {
    if (!caseDoc.case_number || !caseDoc.subject_title || !caseDoc.district) {
      toast.error("Case number, subject and district are required");
      setTab("master");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/cases", caseDoc);
      const caseId = data.case_id;

      for (const p of parties.filter((x) => x.party_name.trim())) {
        await api.post(`/cases/${caseId}/parties`, p);
      }
      for (const p of properties.filter((x) => x.district || x.survey_number)) {
        await api.post(`/cases/${caseId}/properties`, p);
      }
      for (const o of lowerOrders.filter((x) => x.authority_name || x.order_number)) {
        await api.post(`/cases/${caseId}/lower-orders`, o);
      }

      toast.success(`Case ${data.case_number} saved`);
      navigate(continueTo === "documents" ? `/cases/${caseId}/documents` : `/cases/${caseId}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save case");
    } finally {
      setSaving(false);
    }
  };

  const inputCls = "input-base";

  return (
    <Layout breadcrumbs={["SSRD", "Cases", "Create"]}>
      <div className="mb-6">
        <div className="text-[10px] tracking-widest text-muted-foreground uppercase">Case Input Layer</div>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight" data-testid="create-case-title">Create Case</h1>
        <p className="text-sm text-muted-foreground">
          Capture the case master, parties, land details and lower authority order.
          The system will use these as verified facts during Remi8-assisted drafting.
        </p>
      </div>

      <Tabs value={tab} onValueChange={setTab} data-testid="create-case-tabs">
        <TabsList className="grid grid-cols-4">
          <TabsTrigger value="master" data-testid="tab-master">A. Master</TabsTrigger>
          <TabsTrigger value="parties" data-testid="tab-parties">B. Parties</TabsTrigger>
          <TabsTrigger value="land" data-testid="tab-land">C. Land</TabsTrigger>
          <TabsTrigger value="lower" data-testid="tab-lower">D. Lower Order</TabsTrigger>
        </TabsList>

        {/* SECTION A: Master */}
        <TabsContent value="master">
          <Card><CardContent className="p-5 grid md:grid-cols-3 gap-4">
            <Field label="Case Number *"><Input data-testid="cm-case-number" value={caseDoc.case_number} onChange={(e) => setCaseDoc({ ...caseDoc, case_number: e.target.value })} className={inputCls} /></Field>
            <Field label="Case Year *"><Input data-testid="cm-case-year" value={caseDoc.case_year} onChange={(e) => setCaseDoc({ ...caseDoc, case_year: e.target.value })} className={inputCls} /></Field>
            <Field label="Case Type *">
              <Select value={caseDoc.case_type} onValueChange={(v) => setCaseDoc({ ...caseDoc, case_type: v })}>
                <SelectTrigger data-testid="cm-case-type"><SelectValue /></SelectTrigger>
                <SelectContent>{CASE_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Department Name"><Input value={caseDoc.department_name} onChange={(e) => setCaseDoc({ ...caseDoc, department_name: e.target.value })} className={inputCls} /></Field>
            <Field label="Office Name"><Input value={caseDoc.office_name} onChange={(e) => setCaseDoc({ ...caseDoc, office_name: e.target.value })} className={inputCls} /></Field>
            <Field label="Language"><Input value={caseDoc.language} onChange={(e) => setCaseDoc({ ...caseDoc, language: e.target.value })} className={inputCls} /></Field>
            <Field label="District *"><Input data-testid="cm-district" value={caseDoc.district} onChange={(e) => setCaseDoc({ ...caseDoc, district: e.target.value })} className={inputCls} /></Field>
            <Field label="Taluka"><Input value={caseDoc.taluka} onChange={(e) => setCaseDoc({ ...caseDoc, taluka: e.target.value })} className={inputCls} /></Field>
            <Field label="Village"><Input value={caseDoc.village} onChange={(e) => setCaseDoc({ ...caseDoc, village: e.target.value })} className={inputCls} /></Field>
            <Field label="Subject / Matter Title *" wide>
              <Textarea data-testid="cm-subject" value={caseDoc.subject_title} onChange={(e) => setCaseDoc({ ...caseDoc, subject_title: e.target.value })} rows={2} />
            </Field>
            <Field label="Legal Act"><Input value={caseDoc.legal_act} onChange={(e) => setCaseDoc({ ...caseDoc, legal_act: e.target.value })} className={inputCls} /></Field>
            <Field label="Legal Section / Rule"><Input value={caseDoc.legal_section} onChange={(e) => setCaseDoc({ ...caseDoc, legal_section: e.target.value })} className={inputCls} /></Field>
            <Field label="Filing Date"><Input type="date" value={caseDoc.filing_date} onChange={(e) => setCaseDoc({ ...caseDoc, filing_date: e.target.value })} className={inputCls} /></Field>
            <Field label="Hearing Date"><Input type="date" value={caseDoc.hearing_date} onChange={(e) => setCaseDoc({ ...caseDoc, hearing_date: e.target.value })} className={inputCls} /></Field>
            <Field label="Order Date"><Input type="date" value={caseDoc.order_date} onChange={(e) => setCaseDoc({ ...caseDoc, order_date: e.target.value })} className={inputCls} /></Field>
          </CardContent></Card>
        </TabsContent>

        {/* SECTION B: Parties */}
        <TabsContent value="parties">
          <RepeatableList
            title="Case Parties" testid="parties"
            items={parties} setItems={setParties} empty={emptyParty}
            render={(p, i, update) => (
              <div className="grid md:grid-cols-3 gap-4">
                <Field label="Party Role">
                  <Select value={p.party_role} onValueChange={(v) => update(i, { ...p, party_role: v })}>
                    <SelectTrigger data-testid={`party-role-${i}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{PARTY_ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="Sequence"><Input type="number" value={p.party_sequence} onChange={(e) => update(i, { ...p, party_sequence: +e.target.value })} className={inputCls} /></Field>
                <Field label="Party Name *"><Input data-testid={`party-name-${i}`} value={p.party_name} onChange={(e) => update(i, { ...p, party_name: e.target.value })} className={inputCls} /></Field>
                <Field label="Father / Spouse Name"><Input value={p.father_or_spouse_name} onChange={(e) => update(i, { ...p, father_or_spouse_name: e.target.value })} className={inputCls} /></Field>
                <Field label="Advocate / Representative"><Input value={p.advocate_name} onChange={(e) => update(i, { ...p, advocate_name: e.target.value })} className={inputCls} /></Field>
                <Field label="Address" wide><Textarea value={p.address_line} onChange={(e) => update(i, { ...p, address_line: e.target.value })} rows={2} /></Field>
                <Field label="Village"><Input value={p.village} onChange={(e) => update(i, { ...p, village: e.target.value })} className={inputCls} /></Field>
                <Field label="Taluka"><Input value={p.taluka} onChange={(e) => update(i, { ...p, taluka: e.target.value })} className={inputCls} /></Field>
                <Field label="District"><Input value={p.district} onChange={(e) => update(i, { ...p, district: e.target.value })} className={inputCls} /></Field>
                <Field label="State"><Input value={p.state} onChange={(e) => update(i, { ...p, state: e.target.value })} className={inputCls} /></Field>
                <Field label="PIN Code"><Input value={p.pin_code} onChange={(e) => update(i, { ...p, pin_code: e.target.value })} className={inputCls} /></Field>
                <Field label="Government Authority?">
                  <div className="h-9 flex items-center gap-2">
                    <Switch checked={p.is_government_authority} onCheckedChange={(v) => update(i, { ...p, is_government_authority: v })} />
                    <span className="text-xs text-muted-foreground">{p.is_government_authority ? "Yes" : "No"}</span>
                  </div>
                </Field>
              </div>
            )}
          />
        </TabsContent>

        {/* SECTION C: Land */}
        <TabsContent value="land">
          <RepeatableList
            title="Land / Property Details" testid="properties"
            items={properties} setItems={setProperties} empty={emptyProperty}
            render={(p, i, update) => (
              <div className="grid md:grid-cols-3 gap-4">
                <Field label="District"><Input data-testid={`prop-district-${i}`} value={p.district} onChange={(e) => update(i, { ...p, district: e.target.value })} className={inputCls} /></Field>
                <Field label="Taluka"><Input value={p.taluka} onChange={(e) => update(i, { ...p, taluka: e.target.value })} className={inputCls} /></Field>
                <Field label="Village"><Input value={p.village} onChange={(e) => update(i, { ...p, village: e.target.value })} className={inputCls} /></Field>
                <Field label="Survey Number"><Input data-testid={`prop-survey-${i}`} value={p.survey_number} onChange={(e) => update(i, { ...p, survey_number: e.target.value })} className={inputCls} /></Field>
                <Field label="Block Number"><Input value={p.block_number} onChange={(e) => update(i, { ...p, block_number: e.target.value })} className={inputCls} /></Field>
                <Field label="Old Survey Number"><Input value={p.old_survey_number} onChange={(e) => update(i, { ...p, old_survey_number: e.target.value })} className={inputCls} /></Field>
                <Field label="New Survey Number"><Input value={p.new_survey_number} onChange={(e) => update(i, { ...p, new_survey_number: e.target.value })} className={inputCls} /></Field>
                <Field label="City Survey Number"><Input value={p.city_survey_number} onChange={(e) => update(i, { ...p, city_survey_number: e.target.value })} className={inputCls} /></Field>
                <Field label="Revenue Record Ref"><Input value={p.revenue_record_ref} onChange={(e) => update(i, { ...p, revenue_record_ref: e.target.value })} className={inputCls} /></Field>
                <Field label="Land Area"><Input value={p.land_area} onChange={(e) => update(i, { ...p, land_area: e.target.value })} className={inputCls} /></Field>
                <Field label="Unit">
                  <Select value={p.land_area_unit} onValueChange={(v) => update(i, { ...p, land_area_unit: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{LAND_UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="Land Use Type"><Input value={p.land_use_type} onChange={(e) => update(i, { ...p, land_use_type: e.target.value })} className={inputCls} placeholder="Agricultural / NA / Residential" /></Field>
                <Field label="Disputed Property Description" wide>
                  <Textarea value={p.disputed_property_description} onChange={(e) => update(i, { ...p, disputed_property_description: e.target.value })} rows={3} />
                </Field>
              </div>
            )}
          />
        </TabsContent>

        {/* SECTION D: Lower Authority */}
        <TabsContent value="lower">
          <RepeatableList
            title="Lower Authority Order Reference" testid="lower-orders"
            items={lowerOrders} setItems={setLowerOrders} empty={emptyLowerOrder}
            render={(o, i, update) => (
              <div className="grid md:grid-cols-3 gap-4">
                <Field label="Authority Level">
                  <Select value={o.authority_level} onValueChange={(v) => update(i, { ...o, authority_level: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{AUTHORITY_LEVELS.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="Authority Name"><Input value={o.authority_name} onChange={(e) => update(i, { ...o, authority_name: e.target.value })} className={inputCls} /></Field>
                <Field label="Office Location"><Input value={o.office_location} onChange={(e) => update(i, { ...o, office_location: e.target.value })} className={inputCls} /></Field>
                <Field label="Order Number"><Input data-testid={`lo-number-${i}`} value={o.order_number} onChange={(e) => update(i, { ...o, order_number: e.target.value })} className={inputCls} /></Field>
                <Field label="Order Date"><Input type="date" value={o.order_date} onChange={(e) => update(i, { ...o, order_date: e.target.value })} className={inputCls} /></Field>
                <Field label="Outcome">
                  <Select value={o.outcome_of_lower_order} onValueChange={(v) => update(i, { ...o, outcome_of_lower_order: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{OUTCOMES.map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="Challenged Order Summary" wide>
                  <Textarea value={o.challenged_order_summary} onChange={(e) => update(i, { ...o, challenged_order_summary: e.target.value })} rows={3} />
                </Field>
              </div>
            )}
          />
        </TabsContent>
      </Tabs>

      <div className="mt-6 flex flex-wrap items-center justify-end gap-2 border-t border-border pt-4">
        <Button variant="outline" disabled={saving} onClick={() => save()} data-testid="save-case-button" className="gap-2">
          <Save className="size-4" /> Save Case
        </Button>
        <Button disabled={saving} onClick={() => save("documents")} data-testid="save-continue-button" className="gap-2">
          Save and Continue to Documents <ArrowRight className="size-4" />
        </Button>
      </div>
    </Layout>
  );
}

function Field({ label, children, wide = false }) {
  return (
    <div className={wide ? "md:col-span-3" : ""}>
      <Label className="text-xs text-muted-foreground">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function RepeatableList({ title, testid, items, setItems, empty, render }) {
  const update = (idx, val) => setItems(items.map((x, i) => (i === idx ? val : x)));
  const add = () => setItems([...items, empty()]);
  const remove = (idx) => setItems(items.filter((_, i) => i !== idx));
  return (
    <Card data-testid={`repeatable-${testid}`}><CardContent className="p-5 space-y-5">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">{title}</h3>
        <Button variant="outline" size="sm" onClick={add} className="gap-1" data-testid={`add-${testid}-button`}><Plus className="size-3.5" /> Add</Button>
      </div>
      {items.map((it, i) => (
        <div key={i} className="border border-border rounded-md p-4 relative">
          <button onClick={() => remove(i)} className="absolute top-2 right-2 text-muted-foreground hover:text-red-700" data-testid={`remove-${testid}-${i}`}>
            <Trash2 className="size-4" />
          </button>
          <div className="text-[10px] tracking-widest text-muted-foreground uppercase mb-3">Entry #{i + 1}</div>
          {render(it, i, update)}
        </div>
      ))}
    </CardContent></Card>
  );
}
